


import { Component, Prop, Ref, Vue } from "vue-property-decorator";

import Calendar from "primevue/calendar";

import { $t, I18n } from "App/IOC";

import Modal from "Web/Components/Common/Modal.vue";

/* LE CHOIX D'UNE PERIODE DES STATISTIQUES, comme le Desktop ( Fred, 22/10/2026 ;
   `src/Web/Components/Common/PeriodPicker.vue` ).

   Un bouton « 01/01/2026 a 31/12/2026 » ; au clic, les raccourcis a gauche et le
   calendrier sur deux mois a droite. Le choix part des que la periode est
   complete, sans bouton « Appliquer ». Le premier raccourci vide la periode
   ( « Depuis le premier envoi » ).

   La valeur circule en `[ "YYYY-MM-DD", "YYYY-MM-DD" ]` ( dates locales ), `[]` =
   toute la periode : c'est ce que les routes attendent. Le panneau est porte dans
   `#modals` et place par `v-strict-snap`, comme `Input/DatePicker.vue`, et le
   calendrier porte l'habillage `.ds-calendar`. */
// @ts-ignore
@Component({
    // @ts-ignore
    components: {
        Calendar,
        Modal
    }
})
export default class PeriodPicker extends Vue {
    @Prop( { default: (): string[] => [] } )
    readonly Value!: string[];

    @Prop( { default: "" } )
    readonly AllLabel!: string;

    /* Le libelle du bouton tant que rien n'est choisi ( « Personnalise » ). */
    @Prop( { default: "" } )
    readonly Placeholder!: string;

    @Ref( "container" )
    private readonly container?: HTMLElement;

    @Ref( "panel" )
    private readonly panel?: HTMLElement;

    private opened: boolean = false;
    private active: string | null = null;

    /* Le calendrier tient sa selection en cours ( une borne, puis deux ). */
    private range: ( Date | null )[] = [];

    beforeDestroy(): void {
        window.removeEventListener( "mousedown", this.onOutsideClick );
    }

    private get language(): string {
        return I18n.Locale.Id;
    }

    private get presets(): { key: string; label: string }[] {
        const keys = [
            "ThisWeek", "LastWeek", "ThisMonth", "LastMonth",
            "Last3Months", "Last6Months", "ThisYear", "LastYear"
        ];
        const list = keys.map( ( key: string ): { key: string; label: string } => ( {
            key, label: $t( `NEWSLETTERS_period${ key }` ).toString()
        } ) );

        return ( this.AllLabel !== "" ) ? [ { key: "all", label: this.AllLabel }, ...list ] : list;
    }

    private get label(): string {
        const from = parse( this.Value[ 0 ] || "" );
        const to = parse( this.Value[ 1 ] || "" );

        if( from === null )
            return this.Placeholder || this.AllLabel;

        const show = ( date: Date ): string =>
            `${ pad( date.getDate() ) }/${ pad( date.getMonth() + 1 ) }/${ date.getFullYear() }`;

        return ( to !== null )
            ? $t( "NEWSLETTERS_periodRange", { from: show( from ), to: show( to ) } ).toString()
            : show( from );
    }

    private get locale(): any {
        const months: string[] = [];
        const monthsShort: string[] = [];
        const days: string[] = [];
        const daysShort: string[] = [];

        for( let month = 0; month < 12; month++ ) {
            const date = new Date( 2026, month, 1 );

            months.push( date.toLocaleDateString( this.language, { month: "long" } ) );
            monthsShort.push( date.toLocaleDateString( this.language, { month: "short" } ) );
        }

        /* Le 04/01/2026 est un dimanche ( voir `Input/DatePicker.vue` ). */
        for( let day = 0; day < 7; day++ ) {
            const date = new Date( 2026, 0, 4 + day );

            days.push( date.toLocaleDateString( this.language, { weekday: "long" } ) );
            daysShort.push( date.toLocaleDateString( this.language, { weekday: "short" } ) );
        }

        return {
            firstDayOfWeek: 1,
            dayNames: days,
            dayNamesShort: daysShort,
            dayNamesMin: daysShort,
            monthNames: months,
            monthNamesShort: monthsShort,
            today: "",
            clear: "",
            dateFormat: "yy-mm-dd",
            weekHeader: ""
        };
    }

    private toggle(): void {
        this.opened ? this.close() : this.open();
    }

    private open(): void {
        this.range = this.Value
            .map( ( value: string ): Date | null => parse( value ) )
            .filter( ( value: Date | null ): boolean => value !== null );
        this.opened = true;
        window.addEventListener( "mousedown", this.onOutsideClick );
    }

    private close(): void {
        this.opened = false;
        window.removeEventListener( "mousedown", this.onOutsideClick );
    }

    private onOutsideClick( event: Event ): void {
        const target = event.target as Node;

        if( this.container !== undefined && this.container.contains( target ) )
            return;

        if( this.panel !== undefined && this.panel.contains( target ) )
            return;

        this.close();
    }

    /* Memes bornes que le Desktop ( et l'ecran Statistiques ). */
    private apply( key: string ): void {
        this.active = key;

        if( key === "all" ) {
            this.$emit( "Input", [] );
            this.close();
            return;
        }

        const now = new Date();
        const y = now.getFullYear();
        const m = now.getMonth();
        const d = now.getDate();
        const day = ( yr: number, mo: number, dy: number ): Date => new Date( yr, mo, dy );
        const weekday = now.getDay() === 0 ? 7 : now.getDay();

        const map: { [ name: string ]: [ Date, Date ] } = {
            ThisWeek: [ day( y, m, d - weekday + 1 ), day( y, m, d - weekday + 7 ) ],
            LastWeek: [ day( y, m, d - weekday - 6 ), day( y, m, d - weekday ) ],
            ThisMonth: [ day( y, m, 1 ), day( y, m + 1, 0 ) ],
            LastMonth: [ day( y, m - 1, 1 ), day( y, m, 0 ) ],
            Last3Months: [ day( y, m - 3, d ), day( y, m, d ) ],
            Last6Months: [ day( y, m - 6, d ), day( y, m, d ) ],
            ThisYear: [ day( y, 0, 1 ), day( y, 11, 31 ) ],
            LastYear: [ day( y - 1, 0, 1 ), day( y - 1, 11, 31 ) ]
        };

        if( map[ key ] !== undefined )
            this.$emit( "Input", map[ key ].map( format ) );

        this.close();
    }

    /* Le calendrier : la periode part quand les deux bornes sont posees. */
    private picked(): void {
        this.$nextTick( (): void => {
            const [ from, to ] = this.range;

            if( from instanceof Date && to instanceof Date ) {
                this.active = null;
                this.$emit( "Input", [ format( from ), format( to ) ] );
                this.close();
            }
        } );
    }
}

function pad( value: number ): string {
    return String( value ).padStart( 2, "0" );
}

function format( date: Date ): string {
    return `${date.getFullYear()}-${pad( date.getMonth() + 1 )}-${pad( date.getDate() )}`;
}

/* `new Date( "2026-10-06" )` se lit en UTC : la date locale se construit a la main. */
function parse( value: string ): Date | null {
    const parts = value.split( "-" ).map( Number );

    if( parts.length !== 3 || parts.some( isNaN ) )
        return null;

    return new Date( parts[ 0 ], parts[ 1 ] - 1, parts[ 2 ] );
}
